import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@mui/styles'
import moment from 'moment'
import {Spacing, Colors, Typography} from '../../assets/styles'
import {borderRadiusMedium} from '../../assets/styles/border'
import {formatElapsedTime} from '../../Utils/dateTimeUtil'

const DATE_FORMAT = 'MMM D, YYYY'
const CHIP_SEPARATOR = ' / '

/**
 *
 * @param {Object} props
 * @param {import('./exitSurveyVideoData').VideoResponse} props.response
 * @param {function} props.onPlay
 */
function ExitSurveyVideoCard({response, onPlay}) {
  const classes = useStyles()
  const {thumbnailUrl, durationSeconds} = response.video
  const [thumbnailFailed, setThumbnailFailed] = useState(false)
  useEffect(() => setThumbnailFailed(false), [thumbnailUrl])

  // The card shows one chip of reasons and one of sub-reasons, each slash-separated — the design does not
  // pair a sub-reason back to the reason it came from, so both collapse across every group.
  const reasonGroups = response.reasonGroups || []
  const reasonLabels = reasonGroups.map(group => group.label).join(CHIP_SEPARATOR)
  const subReasonLabels = reasonGroups.flatMap(group => (group.subReasons || []).map(sub => sub.label)).join(CHIP_SEPARATOR)

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {response.residentName}
        {response.unitNumber ? <span className={classes.unit}>{`, ${response.unitNumber}`}</span> : null}
      </div>
      <div className={classes.submittedAt}>{`Submitted ${moment(response.submittedAt).format(DATE_FORMAT)}`}</div>

      <div className={classes.reasons}>
        {reasonLabels ? <span className={classes.macroChip}>{reasonLabels}</span> : null}
        {subReasonLabels ? <span className={classes.subChip}>{subReasonLabels}</span> : null}
      </div>

      <div className={classes.preview} onClick={() => onPlay(response)} role={'button'}>
        {thumbnailUrl && !thumbnailFailed ? (
          <img className={classes.thumbnail} src={thumbnailUrl} alt={''} onError={() => setThumbnailFailed(true)} />
        ) : (
          <div className={classes.thumbnailPlaceholder} />
        )}
        <span className={classes.playScrim}>
          <span className={classes.playCircle}>
            <span className={classes.playTriangle} />
          </span>
        </span>
        {durationSeconds ? <span className={classes.durationBadge}>{formatElapsedTime(durationSeconds)}</span> : null}
      </div>
    </div>
  )
}

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: Spacing.spaceMedium,
    backgroundColor: Colors.rxrWhiteColor,
    border: `1px solid ${Colors.rxrGreyColor}`,
    borderRadius: borderRadiusMedium,
  },
  name: {
    fontSize: Typography.fontSizeLarge,
    fontWeight: Typography.mediumFontWeight,
    color: Colors.rxrBlackColor,
  },
  unit: {
    fontWeight: Typography.lightFontWeight,
  },
  submittedAt: {
    marginTop: Spacing.spaceExtraExtraSmall,
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.lightFontWeight,
    color: Colors.rxrDarkGreyColor,
  },
  reasons: {
    display: 'flex',
    flexDirection: 'column',
    // Without this the chips stretch to the card width instead of hugging their text.
    alignItems: 'flex-start',
    gap: Spacing.spaceExtraExtraSmall,
    margin: `${Spacing.spaceSmall}px 0`,
  },
  macroChip: {
    padding: `4px ${Spacing.spaceSmall}px`,
    borderRadius: borderRadiusMedium,
    border: `1px solid ${Colors.rxrPrimaryColor}`,
    backgroundColor: Colors.rxrTint15Color,
    color: Colors.rxrPrimaryColor,
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.mediumFontWeight,
  },
  subChip: {
    padding: `4px ${Spacing.spaceSmall}px`,
    borderRadius: borderRadiusMedium,
    border: `1px solid ${Colors.rxrGreyColor}`,
    backgroundColor: Colors.nearWhiteColor,
    color: Colors.rxrDarkGreyColor,
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.lightFontWeight,
  },
  preview: {
    position: 'relative',
    marginTop: 'auto',
    width: '100%',
    aspectRatio: '3 / 4',
    borderRadius: borderRadiusMedium,
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: Colors.rxrLightGreyColor,
  },
  thumbnail: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbnailPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: Colors.rxrMonotoneLightGreyColor,
  },
  playScrim: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.15)',
  },
  playCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    border: `2px solid ${Colors.rxrWhiteColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playTriangle: {
    marginLeft: 4,
    borderTop: '12px solid transparent',
    borderBottom: '12px solid transparent',
    borderLeft: `18px solid ${Colors.rxrWhiteColor}`,
  },
  durationBadge: {
    position: 'absolute',
    bottom: Spacing.spaceSmall,
    right: Spacing.spaceSmall,
    padding: `2px ${Spacing.spaceExtraSmall}px`,
    borderRadius: Spacing.spaceExtraExtraSmall,
    backgroundColor: Colors.blackOverlay50,
    color: Colors.rxrWhiteColor,
    fontSize: Typography.fontSizeSmall,
  },
})

ExitSurveyVideoCard.propTypes = {
  response: PropTypes.shape({
    id: PropTypes.string.isRequired,
    residentName: PropTypes.string.isRequired,
    unitNumber: PropTypes.string,
    submittedAt: PropTypes.string,
    reasonGroups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        subReasons: PropTypes.array.isRequired,
      }),
    ).isRequired,
    video: PropTypes.shape({
      url: PropTypes.string.isRequired,
      thumbnailUrl: PropTypes.string,
      durationSeconds: PropTypes.number,
    }).isRequired,
  }).isRequired,
  onPlay: PropTypes.func.isRequired,
}

export default ExitSurveyVideoCard
